import {useState, useRef, useEffect} from 'react';
import {CommunicationManager} from './Tools/CommunicationManager';
import SearchTermSuggestions from './Component/SearchTermSuggestions';
import ArticleSearchResults from './Component/ArticleSearchResults';
import InformationSearchResults from './Component/InformationSearchResults';
import GenericSearchResults from './Component/GenericSearchResults';
import FaqSearchResults from './Component/FaqSearchResults';
import PromotedSearchResults from './Component/PromotedSearchResults';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

export default function MainSearchApp() {
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [queryString, setQueryString] = useState('');
  const [renderDom, setRenderDom] = useState(false);
  const [articleResults, setArticleResults] = useState([]);
  const [informationResults, setInformationResults] = useState([]);
  const [genericResults, setGenericResults] = useState([]);
  const [faqResults, setFaqResults] = useState([]);
  const [promotedResults, setPromotedResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [searchRequestTimeout, setSearchRequestTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('nl');

  const commManager = new CommunicationManager('headless_search');

  function shoutSearchQuery(query) {
    console.debug('Sending [' + query + '] to datalayer as event flink_search_query')
    window.dataLayer.push({
      'event': 'flink_search_query',
      'fsq_query': query
    });
  }

  // Centralized reset function
  function resetResults() {
    setArticleResults([]);
    setInformationResults([]);
    setGenericResults([]);
    setFaqResults([]);
    setPromotedResults([]);
    setTotalResults(0);
  }

  // Centralized reset filter function
  function resetFilterIfNeeded() {
    if (filterType !== 'all') {
      setFilterType('all');
    }
  }

  // Simplified reset logic
  if (searchTerm.length < MIN_SEARCH_LENGTH) {
    if (articleResults.length || genericResults.length || informationResults.length ||
      faqResults.length || promotedResults.length || totalResults !== 0) {
      resetResults();
    }
  } else {
    let newQueryString = 'q=' + encodeURIComponent(searchTerm);
    if (newQueryString !== queryString) {
      setQueryString(newQueryString);
    }
  }

  function handleSearch(e) {
    let searchTerm = searchInputRef.current.value.replace('*', '');

    resetFilterIfNeeded();
    commManager.sendMessage({searchTerm});
    setSearchTerm(searchTerm);
  }

  function handleClearSearch() {
    setSearchTerm('');
    resetResults();
    setFilterType('all');
    setIsLoading(false);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    commManager.sendMessage({searchTerm: ''});
  }

  function handleFilterClick(type) {
    setFilterType(type);

    setTimeout(() => {
      const resultsHeader = document.querySelector('.search-app__header');
      if (resultsHeader) {
        resultsHeader.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    }, 100);
  }

  function handleFormSubmit(e) {
    e.preventDefault();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTerm.length > MIN_SEARCH_LENGTH) {
        setQueryString('q=' + encodeURIComponent(searchTerm));
      }
    }
  }

  // Add this function to handle going back to all results
  function handleBackToAllResults() {
    setFilterType('all');

    setTimeout(() => {
      const resultsHeader = document.querySelector('.search-app__header');
      if (resultsHeader) {
        resultsHeader.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    }, 100);
  }

  const handleSuggestionClick = (value) => {
    resetFilterIfNeeded();
    setSearchTerm(value);
  };

  // Handle focus management properly in useEffect
  useEffect(() => {
    if (renderDom === true && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [renderDom]);

  // Monitor language changes
  useEffect(() => {
    function checkLanguageChange() {
      const htmlLang = document.documentElement.lang || 'nl';
      if (htmlLang !== currentLanguage) {
        setCurrentLanguage(htmlLang);
      }
    }

    // Check immediately
    checkLanguageChange();

    // Set up observer for language changes
    const observer = new MutationObserver(() => {
      checkLanguageChange();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang']
    });

    return () => observer.disconnect();
  }, [currentLanguage]);

  // Execute this only during initial load
  useEffect(() => {
    commManager.receiveMessage((message) => {
      if ('searchTerm' in message) {
        console.debug('Received searchTerm ' + message.searchTerm);
        setSearchTerm(message.searchTerm);
      }
    });
  }, []);

  useEffect(() => {
    if (!queryString) {
      return;
    }

    let isMounted = true;
    resetResults();

    if (searchRequestTimeout) {
      clearTimeout(searchRequestTimeout);
    }

    setSearchRequestTimeout(setTimeout(() => {
      setIsLoading(true);

      fetch('/api/search/public?' + queryString)
        .then(response => response.json())
        .then(data => {
          if (isMounted) {
            setArticleResults(data.articles?.data?.results || []);
            setInformationResults(data.information?.data?.results || []);
            setGenericResults(data.generic?.data?.results || []);
            setFaqResults(data.faqs?.data?.results || []);
            setPromotedResults(data.promoted?.data?.results || []);
            setTotalResults(typeof data.total === 'number' ? data.total : 0);
            shoutSearchQuery(searchTerm);
            setIsLoading(false);
          }
        })
        .catch(error => {
          console.error('Search failed:', error);
          if (isMounted) {
            setIsLoading(false);
          }
        });
    }, SEARCH_DEBOUNCE_MS));

    return () => {
      isMounted = false;
    };
  }, [queryString, searchTerm]);

  return (
    <>
      <form ref={searchResultsRef} onSubmit={handleFormSubmit} data-fcs-region="search_app"
            className={'search-block__search-app ' + (searchTerm.length ? 'search-block__search-app--is-active' : '') + (searchTerm.length > MIN_SEARCH_LENGTH ? ' search-block__search-app--is-searching' : '')}>
        <div className="grid">
          <div className="search-app__search-app-form grid__column grid__column--two-thirds">
            <div className="form--app">
              <div className="form-item">
                <div className="main-search main-search--overlay">
                  <div className="main-search__input-wrapper">
                    <label className={'sr-only'} htmlFor="overlay-search-input">Zoek op trefwoord</label>
                    <input
                      type="search"
                      id="overlay-search-input"
                      name="keys"
                      onKeyDown={handleKeyDown}
                      title="Geef de woorden op waarnaar u wilt zoeken."
                      placeholder="Stel je vraag"
                      onChange={handleSearch}
                      ref={searchInputRef}
                      value={searchTerm}
                      className="main-search__input form-search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck="false"
                    />
                    {searchTerm.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className={"main-search__clear-button button--stripped"}
                        aria-label="Zoekopdracht wissen"
                      >
                        Wis
                      </button>
                    )}
                    <svg className="icon icon-search" aria-hidden="true">
                      <use href="#icon-search"/>
                    </svg>
                  </div>
                  {currentLanguage !== 'nl' && (
                    <div className="search-app__language-notice">
                      <p>Zoek op een Nederlands woord om de resultaten in je geselecteerde taal te zien.</p>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>

          <SearchTermSuggestions
            searchTerm={searchTerm}
            useSuggestion={handleSuggestionClick}
          />

          {isLoading && searchTerm.length > MIN_SEARCH_LENGTH && (
            <div className="search-app__loader">
              <span className="sr-only">Zoeken..</span>
            </div>
          )}

          {!isLoading && searchTerm.length > MIN_SEARCH_LENGTH && (
            <div className="search-app__header grid__column grid__column--full-width">
              <h2 className={'search-app__thisterm'}>
                {
                  filterType === 'all' ? totalResults :
                    filterType === 'article' ? (articleResults || []).length :
                      filterType === 'generic' ? (genericResults || []).length :
                        filterType === 'information' ? (informationResults || []).length :
                          filterType === 'faq' ? (faqResults || []).length :
                            totalResults
                } Resultaten voor "{searchTerm}"
                {filterType === 'article' && (<span> in Nieuws & Artikelen</span>)}
                {filterType === 'generic' && (<span> in pagina's</span>)}
                {filterType === 'information' && (<span> in Informatie & Regelen</span>)}
                {filterType === 'faq' && (<span> in Veelgestelde vragen</span>)}
              </h2>
            </div>
          )}

          {searchTerm.length > MIN_SEARCH_LENGTH && (
            (filterType === 'all' && totalResults === 0) ||
            (filterType === 'article' && (!articleResults || articleResults.length === 0)) ||
            (filterType === 'generic' && (!genericResults || genericResults.length === 0)) ||
            (filterType === 'information' && (!informationResults || informationResults.length === 0)) ||
            (filterType === 'faq' && (!faqResults || faqResults.length === 0))
          ) && (
            <div className="search-app__no-results grid__column grid__column--full-width">
              <h3>Helaas, de zoekopdracht heeft geen resultaten opgeleverd.</h3>
              <p>Controleer de spelling of probeer een andere zoekterm. Kan je het niet vinden, ga naar <a href={'/contact'}>onze contact pagina</a>.</p>
            </div>
          )}

        </div>

        <div className={"search-app__results grid"}>
          {filterType !== 'all' ? (
            <div className={"grid__column grid__column--full-width"}>
              <button
                onClick={handleBackToAllResults}
                className="search-app__back-button button--stripped button--anim-reversed"
                data-fcs-elm-type="back-to-all"
              >
                <span className="button__icon">
                  <svg className="icon icon-arrow-left" aria-hidden="true">
                    <use href="#icon-arrow-left"/>
                  </svg>
                </span>
                <span className="button__text">
                  Terug naar alle zoekresultaten
                </span>
              </button>
            </div>
          ) : null}

          {!isLoading && filterType === 'all' && searchTerm.length > MIN_SEARCH_LENGTH &&
            <ul className="search-results grid__column--full-width subgrid">
              {(promotedResults || []).length > 0 &&
                <div className={'search-results__promoted grid__column--promoted grid__column grid__column--one-third'}>
                  <div className={"search-results__item search-results__item--promoted"} key={'promoted-results'}
                       data-fcs-region="promoted_results">
                    <div className={"search-results__item__header"}>
                      <h3 className={"search-results__heading"}>Uitgelicht</h3>
                    </div>
                    <PromotedSearchResults key="promoted-search-results" searchTerm={searchTerm} searchResults={promotedResults} limit={2}/>
                  </div>
                </div>
              }

              {(informationResults || []).length > 0 &&
                <li className={"search-results__item search-results__item--information grid__column grid__column--two-thirds"} key={'information-results'}
                    data-fcs-region="information_results">
                  <div className={"search-results__item__header"}>
                    <h3 className={"search-results__heading"}>Gevonden in Informatie & Regelen</h3>
                  </div>
                  <InformationSearchResults key="information-search-results" searchTerm={searchTerm} searchResults={informationResults} limit={3}/>
                  {informationResults.length > 3 && (
                    <div className={'search-results__item__footer'}>
                      <button className={'button--stripped'} onClick={() => handleFilterClick('information')}
                              data-fcs-elm-type="filter-information">
                        Meer <span className={'sr-only'}>resultaten in informatie en regelen</span>
                      </button>
                    </div>
                  )}
                </li>
              }

              {(faqResults || []).length > 0 &&
                <li className={"search-results__item search-results__item--faq grid__column grid__column--two-thirds"} key={'faq-results'}
                    data-fcs-region="faq_results">
                  <div className={"search-results__item__header"}>
                    <h3 className={"search-results__heading"}>Gevonden veelgestelde vragen</h3>
                  </div>
                  <FaqSearchResults key="faq-search-results" searchTerm={searchTerm} searchResults={faqResults} limit={5}/>
                  {faqResults.length > 5 && (
                    <div className={'search-results__item__footer'}>
                      <button className={'button--stripped'} onClick={() => handleFilterClick('faq')}
                              data-fcs-elm-type="filter-faq">
                        Meer <span className={'sr-only'}>resultaten in veelgestelde vragen</span>
                      </button>
                    </div>
                  )}
                </li>
              }

              {(genericResults || []).length > 0 &&
                <li className={"search-results__item search-results__item--generic grid__column grid__column--two-thirds"} key={'generic-results'}
                    data-fcs-region="generic_results">
                  <div className={"search-results__item__header"}>
                    <h3 className={"search-results__heading"}>Gevonden pagina's</h3>
                  </div>
                  <GenericSearchResults key="generic-search-results" searchTerm={searchTerm} searchResults={genericResults} limit={3}/>
                  {genericResults.length > 3 && (
                    <div className={'search-results__item__footer'}>
                      <button className={'button--stripped'} onClick={() => handleFilterClick('generic')}
                              data-fcs-elm-type="filter-generic">
                        Meer <span className={'sr-only'}>resultaten in pagina's</span>
                      </button>
                    </div>
                  )}
                </li>
              }

              {(articleResults || []).length > 0 &&
                <li className={"search-results__item search-results__item--article grid__column grid__column--full-width"} key={'article-results'}
                    data-fcs-region="article_results">
                  <div className={"search-results__item__header js-button-target-default"}>
                    <h3 className={"search-results__heading"}>Gevonden in Nieuws & Artikelen</h3>
                  </div>
                  <ArticleSearchResults key="article-search-results" searchTerm={searchTerm} searchResults={articleResults} limit={3}/>
                  {articleResults.length > 3 && (
                    <div className={'search-results__item__footer'}>
                      <button className={'button--stripped'} onClick={() => handleFilterClick('article')} data-fcs-elm-type="filter-article">
                        Meer <span className={'sr-only'}>resultaten in nieuws & artikelen</span>
                      </button>
                    </div>
                  )}
                </li>
              }
            </ul>
          }

          {/* FILTERED RESULTS */}
          {/* Render More results for each filter, at the moment using the same component, this may change*/}
          {filterType === 'information' && searchTerm.length > MIN_SEARCH_LENGTH && (
            <ul className="search-results--filtered grid__column grid__column--two-thirds">
              <li className={"search-results__item search-results__item--information"} key={'information-results'}
                  data-fcs-region="information_results_full">
                <InformationSearchResults key="information-search-results-full" searchTerm={searchTerm} searchResults={informationResults} limit={1000}/>
              </li>
            </ul>
          )}

          {filterType === 'faq' && searchTerm.length > MIN_SEARCH_LENGTH && (
            <ul className="search-results--filtered grid__column grid__column--two-thirds">
              <li className={"search-results__item search-results__item--faq"} key={'faq-results'} data-fcs-region="faq_results_full">
                <FaqSearchResults key="faq-search-results-full" searchTerm={searchTerm} searchResults={faqResults} limit={1000}/>
              </li>
            </ul>
          )}

          {filterType === 'generic' && searchTerm.length > MIN_SEARCH_LENGTH && (
            <ul className="search-results--filtered grid__column grid__column--two-thirds">
              <li className={"search-results__item search-results__item--generic"} key={'generic-results'}
                  data-fcs-region="generic_results_full">
                <GenericSearchResults key="generic-search-results-full" searchTerm={searchTerm} searchResults={genericResults} limit={1000}/>
              </li>
            </ul>
          )}

          {filterType === 'article' && searchTerm.length > MIN_SEARCH_LENGTH && (
            <ul className="search-results--filtered grid__column grid__column--full-width">
              <li className={"search-results__item search-results__item--article"} key={'article-results'}
                  data-fcs-region="article_results_full">
                <ArticleSearchResults key="article-search-results-full" searchTerm={searchTerm} searchResults={articleResults} limit={1000}/>
              </li>
            </ul>
          )}
        </div>
      </form>
    </>
  );
}
